/**
 * WSIECreatePanel — Full-form sticky note creator.
 * Opens when the user long-presses the FAB or clicks "New Note" from the manager.
 */
import { useState } from 'react';
import { X, StickyNote } from 'lucide-react';
import {
  useWSIEStore, makeStickyNote,
  NOTE_TYPE_META, NOTE_COLORS,
  type NoteType, type NoteColor, type NoteStyle, type NotePriority,
} from '../../stores/wsie.store.js';
import { useAuthStore } from '../../stores/auth.store.js';

const NOTE_TYPES   = Object.keys(NOTE_TYPE_META) as NoteType[];
const NOTE_COLOR_KEYS = Object.keys(NOTE_COLORS) as NoteColor[];
const STYLES: NoteStyle[]     = ['tape', 'pin', 'magnet', 'bare'];
const PRIORITIES: NotePriority[] = ['low', 'medium', 'high', 'critical'];

const PRIORITY_COLORS: Record<NotePriority, string> = {
  low: '#4ade80', medium: '#fbbf24', high: '#f97316', critical: '#f87171',
};

export function WSIECreatePanel({ onClose }: { onClose: () => void }) {
  const { addNote } = useWSIEStore();
  const user = useAuthStore((s) => s.user?.email ?? 'user');

  const [type, setType]         = useState<NoteType>('reminder');
  const [color, setColor]       = useState<NoteColor>('yellow');
  const [style, setStyle]       = useState<NoteStyle>('tape');
  const [priority, setPriority] = useState<NotePriority>('medium');
  const [title, setTitle]       = useState('');
  const [content, setContent]   = useState('');
  const [dueDate, setDueDate]   = useState('');

  const c    = NOTE_COLORS[color];
  const meta = NOTE_TYPE_META[type];

  const handleSave = () => {
    const note = makeStickyNote({
      type, color, style, priority,
      title:     title.trim()   || meta.label,
      content:   content.trim() || '',
      creator:   user,
      dueDate:   dueDate || null,
      anchorRoute: window.location.hash.replace('#', '') || 'workspace',
      anchorLabel: document.title || 'Workspace',
    });
    addNote(note);
    onClose();
  };

  return (
    <div
      data-wsie-ui="1"
      style={{
        position: 'fixed', inset: 0, zIndex: 20200,
        background: 'rgba(6,10,20,0.75)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
        background: '#0d1324', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16, padding: 28,
        boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StickyNote size={18} color="#fbbf24" />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>New Sticky Note</span>
          </div>
          <button onClick={onClose}
            style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#94a3b8', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
            <X size={14} />
          </button>
        </div>

        {/* Type selector */}
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', marginBottom: 6 }}>TYPE</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {NOTE_TYPES.map((t) => {
            const m = NOTE_TYPE_META[t];
            return (
              <button key={t} onClick={() => { setType(t); setColor(m.color); }}
                style={{
                  padding: '5px 10px', borderRadius: 7, border: `1px solid ${type === t ? NOTE_COLORS[m.color].accent : 'rgba(255,255,255,0.06)'}`,
                  background: type === t ? `${NOTE_COLORS[m.color].accent}18` : 'rgba(255,255,255,0.02)',
                  color: type === t ? NOTE_COLORS[m.color].accent : '#64748b',
                  fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}>
                {m.icon} {m.label}
              </button>
            );
          })}
        </div>

        {/* Title */}
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', marginBottom: 6 }}>TITLE</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={meta.label}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
            color: '#f1f5f9', fontSize: 13, outline: 'none', boxSizing: 'border-box',
            marginBottom: 12,
          }}
        />

        {/* Content */}
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', marginBottom: 6 }}>CONTENT</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Add details…"
          rows={4}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
            color: '#f1f5f9', fontSize: 13, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
            marginBottom: 12,
          }}
        />

        {/* Color */}
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', marginBottom: 6 }}>COLOR</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {NOTE_COLOR_KEYS.map((col) => (
            <button key={col} onClick={() => setColor(col)} title={col}
              style={{
                width: 24, height: 24, borderRadius: 6, background: NOTE_COLORS[col].bg,
                border: color === col ? `2px solid ${NOTE_COLORS[col].accent}` : `1px solid ${NOTE_COLORS[col].border}55`,
                cursor: 'pointer', boxShadow: color === col ? `0 0 8px ${NOTE_COLORS[col].shadow}` : 'none',
              }} />
          ))}
        </div>

        {/* Priority + Due date row */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', marginBottom: 6 }}>PRIORITY</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {PRIORITIES.map((p) => (
                <button key={p} onClick={() => setPriority(p)}
                  style={{
                    flex: 1, padding: '5px 4px', borderRadius: 6, border: `1px solid ${priority === p ? PRIORITY_COLORS[p] : 'rgba(255,255,255,0.06)'}`,
                    background: priority === p ? `${PRIORITY_COLORS[p]}18` : 'transparent',
                    color: priority === p ? PRIORITY_COLORS[p] : '#475569',
                    fontSize: 10, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
                  }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', marginBottom: 6 }}>DUE DATE</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={{
                width: '100%', padding: '6px 10px', borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
                color: '#f1f5f9', fontSize: 12, outline: 'none', boxSizing: 'border-box',
                colorScheme: 'dark',
              }}
            />
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '9px 0', borderRadius: 9, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            data-testid="wsie-create-save-btn"
            onClick={handleSave}
            style={{
              flex: 2, padding: '9px 0', borderRadius: 9, border: `1px solid ${c.accent}55`,
              background: `${c.accent}22`, color: c.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
            Create Note
          </button>
        </div>
      </div>
    </div>
  );
}
